import React, { useCallback, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import {
  getPlotlyLegendStyle,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import { computeWrAxisDomainFromData, buildWrYTicksForDomain } from "./axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import {
  wrHundredCategoryFallbackShapes,
  wrHundredLegendTraceCategory,
} from "./plotlyReferenceLineLegend";
import { formatDisplayDate } from "../../../../utils/formatDisplayDate";
import { WrLabel, LABEL } from "../../../../utils/samplingDisplayLabels";
import "./wrVsLengthPlotly.css";

const CARD_W = 280;
const CARD_H = 260;
const formatWrPercent = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;

/** Parse graph row `date` (locale string from formatDateTime) for sort / dedupe. */
function dateToMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const d = new Date(value);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function formatHoverDate(x) {
  if (x == null || x === "") return "";
  if (typeof x === "string") return x;
  return formatDisplayDate(x);
}

/**
 * Indices 0..n-1 evenly spread, always including endpoints.
 * Used when there are many distinct session/stocking dates so tick *labels* stay readable.
 */
function pickEvenlySpacedIndices(n, maxTicks) {
  if (n <= 0) return [];
  if (n <= maxTicks) return Array.from({ length: n }, (_, i) => i);
  const idx = new Set([0, n - 1]);
  const inner = maxTicks - idx.size;
  for (let k = 1; k <= inner; k++) {
    idx.add(Math.round((k * (n - 1)) / (inner + 1)));
  }
  return [...idx].sort((a, b) => a - b);
}

export default function AvgWrVsDateChart({ graphData, isMobile }) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const processed = useMemo(() => {
    const sorted = [...graphData].sort((a, b) => {
      const ta = dateToMs(a.date);
      const tb = dateToMs(b.date);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });
    return sorted.filter((d) => d.count > 0 && dateToMs(d.date) != null);
  }, [graphData]);

  /**
   * Categorical x-axis: one slot per distinct calendar day, evenly spaced (legacy Recharts behavior).
   * `categoryarray` is time-sorted; traces use the same canonical label per ms so stocking lines up.
   */
  const xCategoryAxis = useMemo(() => {
    const msToLabel = new Map();
    processed.forEach((row) => {
      const ms = dateToMs(row.date);
      if (ms == null) return;
      const lab = String(row.date ?? "").trim();
      if (!msToLabel.has(ms)) {
        msToLabel.set(ms, lab || formatDisplayDate(ms));
      }
    });
    const sortedMs = [...msToLabel.keys()].sort((a, b) => a - b);
    const categoryarray = sortedMs.map((ms) => msToLabel.get(ms));
    const msToCategoryLabel = new Map(sortedMs.map((ms) => [ms, msToLabel.get(ms)]));
    return { categoryarray, msToCategoryLabel };
  }, [processed]);

  const { categoryarray, msToCategoryLabel } = xCategoryAxis;

  /** Readable tick labels: angle, margin, font; may subsample tick *labels* when many categories. */
  const xAxisTickLayout = useMemo(() => {
    const n = categoryarray.length;
    if (n === 0) {
      return {
        tickvals: null,
        ticktext: null,
        tickangle: 0,
        marginBottom: isMobile ? 100 : 90,
        tickFontSize: isMobile ? 14 : 16,
      };
    }

    const labelCap = isMobile ? 10 : 16;
    const ix = pickEvenlySpacedIndices(n, labelCap);
    const displayLabels = ix.map((i) => categoryarray[i]);

    const tickangle = 0;

    const marginBottom = isMobile
      ? Math.min(200, 100 + Math.min(displayLabels.length * 3, 72))
      : Math.min(200, 96 + Math.min(displayLabels.length * 3, 64));

    const tickFontSize =
      n > 14 ? (isMobile ? 9 : 10) : n > 9 ? (isMobile ? 11 : 12) : isMobile ? 14 : 16;

    return {
      tickvals: displayLabels,
      ticktext: displayLabels,
      tickangle,
      marginBottom,
      tickFontSize,
    };
  }, [categoryarray, isMobile]);

  const species = useMemo(
    () =>
      sortSpeciesForWeightLengthLegend([
        ...new Set(processed.map((d) => d.species || "Combined")),
      ]),
    [processed]
  );

  const traces = useMemo(() => {
    const catX = (r) => {
      const ms = dateToMs(r.date);
      if (ms == null) return String(r.date ?? "");
      return msToCategoryLabel.get(ms) ?? String(r.date ?? "");
    };

    const t = [];
    species.forEach((sp) => {
      const rows = processed.filter((d) => (d.species || "Combined") === sp);
      const samp = rows
        .filter((d) => !d.isStocking)
        .sort((a, b) => (dateToMs(a.date) || 0) - (dateToMs(b.date) || 0));
      const stock = rows
        .filter((d) => d.isStocking)
        .sort((a, b) => (dateToMs(a.date) || 0) - (dateToMs(b.date) || 0));
      const { color: lineColor, symbol: markSym } = getPlotlyLegendStyle(sp, species);
      if (samp.length) {
        const totalCount = samp.reduce((sum, r) => sum + (r.count || 0), 0);
        t.push({
          type: "scatter",
          mode: "lines+markers",
          name: `${sp} (${totalCount})`,
          x: samp.map(catX),
          y: samp.map((r) => r.averageWr),
          error_y: {
            type: "data",
            array: samp.map((r) => r.stdev || 0),
            visible: true,
          },
          line: { color: lineColor, width: 2 },
          marker: { color: lineColor, size: 8, symbol: markSym, line: { color: "white", width: 1 } },
          customdata: samp.map((r) => [
            sp,
            Number(r.count || 0),
            Number(r.stdev || 0),
            lineColor,
            false,
          ]),
          hovertemplate: "<extra></extra>",
        });
      }
      if (stock.length) {
        const stockingFishCount = stock.reduce((sum, r) => sum + (r.count || 0), 0);
        const stockStdev = stock.map((r) => r.stdev || 0);
        const showStockErr = stockStdev.some((s) => Number(s) > 0);
        t.push({
          type: "scatter",
          mode: "markers",
          name: `${sp} (stocked) (${stockingFishCount})`,
          x: stock.map(catX),
          y: stock.map((r) => r.averageWr),
          error_y: {
            type: "data",
            array: stockStdev,
            visible: showStockErr,
          },
          line: { color: "transparent", width: 0 },
          marker: {
            color: lineColor,
            size: 9,
            symbol: markSym,
            line: { color: "white", width: 1 },
          },
          customdata: stock.map((r) => [
            sp,
            Number(r.count || 0),
            Number(r.stdev || 0),
            lineColor,
            true,
          ]),
          hovertemplate: "<extra></extra>",
        });
      }
    });
    const refTrace = wrHundredLegendTraceCategory(categoryarray);
    if (refTrace) t.push(refTrace);
    return t;
  }, [processed, species, msToCategoryLabel, categoryarray]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 520,
    desktopDefault: 700,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const wrYAxis = useMemo(() => {
    const band = [];
    processed.forEach((d) => {
      const w = Number(d.averageWr);
      if (!Number.isFinite(w)) return;
      const s = Number(d.stdev) || 0;
      band.push(w, w - s, w + s);
    });
    const { yMin, yMax } = computeWrAxisDomainFromData(band);
    const yTicks = buildWrYTicksForDomain(yMin, yMax);
    return { yMin, yMax, yTicks };
  }, [processed]);

  const tickVar = "dateAvgWr";

  const onHover = useCallback((eventData) => {
    const pt = eventData?.points?.[0];
    const el = wrapRef.current;
    const evt = eventData?.event;
    if (!pt || el == null || typeof pt.y !== "number" || !Number.isFinite(pt.y)) {
      return;
    }
    const traceMode = String(pt.data?.mode || pt.fullData?.mode || "");
    if (!traceMode.includes("markers")) {
      return;
    }
    const cd = pt.customdata;
    if (!Array.isArray(cd) || cd.length < 4) {
      return;
    }
    const isStocking = cd[4] === true;

    let cx;
    let cy;
    if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
      cx = evt.clientX;
      cy = evt.clientY;
    } else if (pt.bbox) {
      cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
      cy = pt.bbox.y0;
    } else {
      return;
    }

    const speciesName = String(cd[0] ?? "");
    const count = Number(cd[1]);
    const stdev = Number(cd[2]);
    const seriesColor = String(cd[3] ?? "#059669");
    const avgWr = pt.y;
    const dateStr = formatHoverDate(pt.x);
    const aboveStandard = avgWr >= 1;

    const rect = el.getBoundingClientRect();
    const pad = 12;
    let left = cx - rect.left + pad;
    let top = cy - rect.top + pad;
    if (left + CARD_W > rect.width - 8) {
      left = cx - rect.left - CARD_W - pad;
    }
    if (top + CARD_H > rect.height - 8) {
      top = cy - rect.top - CARD_H - pad;
    }
    left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
    top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

    setTip({
      left,
      top,
      species: speciesName,
      seriesColor,
      dateStr,
      avgWr,
      stdev,
      count,
      aboveStandard,
      isStocking,
    });
  }, []);

  const onUnhover = useCallback(() => setTip(null), []);

  return (
    <div
      ref={wrapRef}
      className="avg-wr-vs-date-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: xAxisTickLayout.marginBottom, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          shapes: wrHundredCategoryFallbackShapes(categoryarray),
          xaxis: {
            type: "category",
            categoryorder: "array",
            categoryarray,
            title: {
              ...plotlyAxisTitle("Date", isMobile),
              font: { ...plotlyAxisTitle("Date", isMobile).font, size: isMobile ? 16 : 22 },
            },
            ...(xAxisTickLayout.tickvals && xAxisTickLayout.tickvals.length
              ? {
                  tickmode: "array",
                  tickvals: xAxisTickLayout.tickvals,
                  ticktext: xAxisTickLayout.ticktext,
                }
              : {}),
            tickangle: xAxisTickLayout.tickangle,
            automargin: true,
            ...plotlyAxisBase(isMobile, tickVar),
            tickfont: {
              ...plotlyAxisBase(isMobile, tickVar).tickfont,
              size: xAxisTickLayout.tickFontSize,
            },
          },
          yaxis: {
            title: {
              ...plotlyAxisTitle(LABEL.AVG_WR_PCT_HTML, isMobile),
              font: { ...plotlyAxisTitle(LABEL.AVG_WR_PCT_HTML, isMobile).font, size: isMobile ? 16 : 22 },
            },
            tickmode: "array",
            tickvals: wrYAxis.yTicks,
            ticktext: wrYAxis.yTicks.map((tick) => formatWrPercent(tick)),
            range: [wrYAxis.yMin, wrYAxis.yMax],
            ...plotlyAxisBase(isMobile, tickVar),
            tickfont: { ...plotlyAxisBase(isMobile, tickVar).tickfont, size: isMobile ? 14 : 16 },
          },
          legend: legendLayout.legend,
          hovermode: "closest",
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--avg-wr${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.seriesColor }}
                aria-hidden
              />
              {tip.species}
              {tip.isStocking ? " · Stocked" : ""}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                {tip.isStocking ? "Stocked date:" : "Date:"}
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--date">
                {tip.dateStr}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                Avg <WrLabel />:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--wr">
                {formatWrPercent(tip.avgWr)}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">{LABEL.STD_DEV}:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {Number.isFinite(tip.stdev) ? formatWrPercent(tip.stdev) : "—"}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">{LABEL.NUM}:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--count">
                {Number.isFinite(tip.count) ? String(Math.round(tip.count)) : "—"}
              </span>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.isStocking
              ? tip.aboveStandard
                ? (
                    <>
                      ✓ Stocked fish at or above <WrLabel /> = 100%
                    </>
                  )
                : (
                    <>
                      Stocked fish below <WrLabel /> = 100%
                    </>
                  )
              : tip.aboveStandard
                ? (
                    <>
                      ✓ Sample average at or above <WrLabel /> = 100%
                    </>
                  )
                : (
                    <>
                      Sample average below <WrLabel /> = 100%
                    </>
                  )}
          </div>
        </div>
      )}
    </div>
  );
}
